/**
 * ContactListsTab - Aba "Contatos" da Central de Importação de Listas
 *
 * Conteúdo extraído de src/pages/tenant/Lists.tsx sem alteração de
 * lógica, queries, filtros ou wizard. Apenas removido o wrapper
 * `<div className="flex flex-col h-full">` para integrar com o Hub.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { logger } from '@/lib/logger';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import {
  Upload,
  FileSpreadsheet,
  MoreVertical,
  Trash2,
  Eye,
  Search,
  Calendar,
  Users,
  AlertTriangle,
  RefreshCw,
  UserX,
  X,
  Plus,
  ShieldCheck,
  Megaphone,
} from 'lucide-react';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { useListExport } from '@/components/export/useListExport';

import ImportListWizard from '@/components/tenant/lists/ImportListWizard';
import ListPreviewDialog from '@/components/tenant/lists/ListPreviewDialog';
import BlacklistDialog from '@/components/tenant/lists/BlacklistDialog';
// Wizard de campanhas reaproveitado para criar campanha a partir de uma lista
import { CreateCampaignDialog } from '@/components/tenant/campaigns/CreateCampaignDialog';
// Visibilidade unificada (Listas / Campanhas / Templates)
import ManageVisibilityDialog from '@/components/shared/ManageVisibilityDialog';
import VisibilityBadge from '@/components/shared/VisibilityBadge';
import type { EntityVisibility } from '@/hooks/useEntityVisibility';
import { useBlacklist } from '@/hooks/useBlacklist';

interface CampaignList {
  id: string;
  name: string;
  hash_code: string;
  total_rows: number;
  imported_contacts: number;
  updated_contacts: number;
  skipped_contacts: number;
  columns: string[];
  variable_columns: string[];
  status: string;
  expires_at: string | null;
  created_at: string;
  visibility?: EntityVisibility | null;
}

export default function ContactListsTab() {
  const { currentTenant } = useAuth();
  // Permissões granulares da aba Contatos (lists_contacts)
  const { canCreate, canDelete } = usePermissions();
  const canCreateLists = canCreate('lists_contacts');
  const canDeleteLists = canDelete('lists_contacts');

  // Exportação da lista importada (módulo genérico de exportação de dados)
  const { podeExportar, exportarLista, dialogoExportacao } = useListExport('lists_contacts');
  // Permissão de criação de campanhas (mesma regra usada na tela de Campanhas)
  const canCreateCampaigns = canCreate('campaigns');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [lists, setLists] = useState<CampaignList[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [isWizardOpen, setIsWizardOpen] = useState(false);
  // Abre wizard via ?new=1 (atalho "Nova lista de importação" do Quick Create).
  useAutoOpenFromQuery('1', () => setIsWizardOpen(true));
  const [isBlacklistOpen, setIsBlacklistOpen] = useState(false);
  const [previewList, setPreviewList] = useState<CampaignList | null>(null);
  const [deleteList, setDeleteList] = useState<CampaignList | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [visibilityList, setVisibilityList] = useState<CampaignList | null>(null);
  // Lista escolhida para criar campanha (abre o wizard pré-selecionado)
  const [campaignFromList, setCampaignFromList] = useState<CampaignList | null>(null);
  const [blacklistCount, setBlacklistCount] = useState(0);

  const { fetchBlacklistCount } = useBlacklist();

  useEffect(() => {
    if (!currentTenant?.id) return;
    fetchLists();
    fetchBlacklistCount().then(setBlacklistCount);
  }, [currentTenant?.id, fetchBlacklistCount]);

  const fetchLists = async () => {
    if (!currentTenant?.id) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .rpc('get_campaign_lists', { p_tenant_hint: currentTenant.id });
      if (error) throw error;
      const processedLists: CampaignList[] = (data || []).map((item: any) => ({
        ...item,
        columns: Array.isArray(item.columns) ? (item.columns as string[]) : [],
        variable_columns: Array.isArray(item.variable_columns) ? (item.variable_columns as string[]) : [],
      }));
      setLists(processedLists);
    } catch (err: unknown) {
      const errorCode = (err as { code?: string })?.code;
      const errorMessage = (err as { message?: string })?.message || '';
      logger.error('[ContactListsTab]', `Erro ao carregar listas: code=${errorCode} msg=${errorMessage}`);
      if (errorCode === '42501' || errorMessage.includes('insufficient_privilege')) {
        if (lists.length === 0) {
          toast.error('Erro de permissão ao carregar listas. Tente novamente.');
        }
      } else {
        toast.error('Erro ao carregar listas');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteList = async () => {
    if (!deleteList) return;
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('campaign_lists')
        .delete()
        .eq('id', deleteList.id);
      if (error) throw error;
      toast.success('Lista excluída com sucesso');
      setDeleteList(null);
      fetchLists();
    } catch (err) {
      logger.error('[ContactListsTab] Erro ao excluir lista:', err);
      toast.error('Erro ao excluir lista');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleWizardComplete = () => {
    setIsWizardOpen(false);
    fetchLists();
    queryClient.invalidateQueries({ queryKey: ['campaign-lists'] });
  };

  const filteredLists = lists.filter(
    (list) =>
      list.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      list.hash_code.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const getStatusDisplay = (status: string, expiresAt: string | null) => {
    const isExpired = expiresAt && new Date(expiresAt) < new Date();
    let tone: 'emerald' | 'amber' | 'destructive' | 'muted' = 'muted';
    let label = status;
    if (isExpired || status === 'expired') {
      tone = 'destructive';
      label = 'Expirada';
    } else if (status === 'ready') {
      tone = 'emerald';
      label = 'Pronta';
    } else if (status === 'processing') {
      tone = 'amber';
      label = 'Processando';
    } else if (status === 'pending') {
      tone = 'muted';
      label = 'Pendente';
    }
    return (
      <div className="flex items-center gap-2">
        <StatusDot tone={tone} />
        <span className="text-sm text-foreground">{label}</span>
      </div>
    );
  };

  const getDaysUntilExpiry = (expiresAt: string | null) => {
    if (!expiresAt) return null;
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffDays = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'Expirada';
    if (diffDays === 0) return 'Expira hoje';
    if (diffDays === 1) return 'Expira amanhã';
    return `Expira em ${diffDays} dias`;
  };

  return (
    <>
      {/* Barra de ações + filtros (sem header próprio — o Hub fornece o header global) */}
      <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-b bg-muted/30">
        <div className="flex flex-wrap items-center gap-2 flex-1">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome ou código..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSearchQuery('')}
              className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3 mr-1" />
              Limpar
            </Button>
          )}
          {isLoading && (
            <span className="text-xs text-muted-foreground animate-pulse">Atualizando...</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="xs" onClick={() => setIsBlacklistOpen(true)}>
            <UserX className="mr-1.5" />
            Blacklist
            {blacklistCount > 0 && (
              <Badge variant="secondary" className="ml-1.5">
                {blacklistCount}
              </Badge>
            )}
          </Button>
          <Button variant="outline" size="xs" onClick={fetchLists} disabled={isLoading}>
            <RefreshCw className={`mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          {canCreateLists && (
            <Button size="xs" onClick={() => setIsWizardOpen(true)}>
              <Plus className="mr-1.5" />
              Nova importação
            </Button>
          )}
        </div>
      </div>

      <Card className="m-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Listas de Contatos Importadas
          </CardTitle>
          <CardDescription>Arquivos CSV importados para uso em campanhas</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredLists.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Nenhuma lista importada</p>
              <p className="text-sm">Clique em "Nova importação" para começar</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Código</TableHead>
                  <TableHead className="text-center">Contatos</TableHead>
                  <TableHead className="text-center">Variáveis</TableHead>
                  <TableHead>Visibilidade</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Validade</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLists.map((list) => (
                  <TableRow key={list.id}>
                    <TableCell className="font-medium">{list.name}</TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded">{list.hash_code}</code>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span>{list.total_rows}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {list.variable_columns.length > 0 ? (
                        <div className="flex flex-wrap gap-1 justify-center">
                          {list.variable_columns.slice(0, 3).map((col) => (
                            <Badge key={col} variant="outline" className="text-xs">
                              {col}
                            </Badge>
                          ))}
                          {list.variable_columns.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{list.variable_columns.length - 3}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <VisibilityBadge visibility={list.visibility ?? 'public'} />
                    </TableCell>
                    <TableCell>{getStatusDisplay(list.status, list.expires_at)}</TableCell>
                    <TableCell>
                      {list.expires_at ? (
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span
                            className={
                              new Date(list.expires_at) < new Date()
                                ? 'text-destructive'
                                : 'text-muted-foreground'
                            }
                          >
                            {getDaysUntilExpiry(list.expires_at)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setPreviewList(list)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Visualizar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setVisibilityList(list)}>
                            <ShieldCheck className="h-4 w-4 mr-2" />
                            Visibilidade
                          </DropdownMenuItem>
                          {/* Criar campanha: só para listas prontas/não expiradas e com permissão */}
                          {canCreateCampaigns &&
                            list.status === 'ready' &&
                            !(list.expires_at && new Date(list.expires_at) < new Date()) && (
                              <DropdownMenuItem onClick={() => setCampaignFromList(list)}>
                                <Megaphone className="h-4 w-4 mr-2" />
                                Criar campanha
                              </DropdownMenuItem>
                            )}
                          {podeExportar && (
                            <DropdownMenuItem
                              onClick={() =>
                                exportarLista({ refId: list.id, name: list.name })
                              }
                            >
                              <FileSpreadsheet className="h-4 w-4 mr-2" />
                              Exportar lista
                            </DropdownMenuItem>
                          )}
                          {canDeleteLists && (
                            <DropdownMenuItem
                              onClick={() => setDeleteList(list)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ImportListWizard
        open={isWizardOpen}
        onOpenChange={setIsWizardOpen}
        onComplete={handleWizardComplete}
      />

      {/* Wizard de campanha aberto a partir de uma lista (pré-seleção da lista) */}
      {campaignFromList && currentTenant?.id && (
        <CreateCampaignDialog
          open={!!campaignFromList}
          onOpenChange={(open) => !open && setCampaignFromList(null)}
          tenantId={currentTenant.id}
          initialListId={campaignFromList.id}
        />
      )}



      <BlacklistDialog
        open={isBlacklistOpen}
        onOpenChange={(open) => {
          setIsBlacklistOpen(open);
          if (!open) {
            fetchBlacklistCount().then(setBlacklistCount);
          }
        }}
      />

      {previewList && (
        <ListPreviewDialog
          list={previewList}
          open={!!previewList}
          onOpenChange={(open) => !open && setPreviewList(null)}
        />
      )}

      {/* Visibilidade unificada (Listas) */}
      <ManageVisibilityDialog
        open={!!visibilityList}
        onOpenChange={(open) => !open && setVisibilityList(null)}
        entityType="campaign_list"
        entityId={visibilityList?.id}
        entityLabel={visibilityList?.name}
      />


      <AlertDialog open={!!deleteList} onOpenChange={(open) => !open && setDeleteList(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Excluir Lista
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a lista "{deleteList?.name}"? Esta ação não pode ser
              desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteList}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal genérico de exportação da lista selecionada */}
      {dialogoExportacao}
    </>
  );
}
